import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';

const GREEN = '#2e9f86';
const blankGroup = () => ({ name: '', multi: true, required: false, options: [{ name: '', price: '' }] });

export default function Modifiers() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // group object being created/edited

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ['modifier-groups'],
    queryFn: () => api.get('/api/modifier-groups').then((r) => r.data),
  });

  const save = useMutation({
    mutationFn: (g) => {
      const body = {
        name: g.name,
        multi: !!g.multi,
        required: !!g.required,
        options: (g.options || [])
          .filter((o) => (o.name || '').trim())
          .map((o) => ({ name: o.name.trim(), price: Number(o.price) || 0 })),
      };
      return g._id
        ? api.put(`/api/modifier-groups/${g._id}`, body)
        : api.post('/api/modifier-groups', body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['modifier-groups'] }); setEditing(null); },
    onError: (e) => alert(e.response?.data?.error || 'Could not save'),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/api/modifier-groups/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['modifier-groups'] }),
    onError: (e) => alert(e.response?.data?.error || 'Could not delete'),
  });

  // editor helpers
  const setField = (k, v) => setEditing((g) => ({ ...g, [k]: v }));
  const setOpt = (i, k, v) => setEditing((g) => ({ ...g, options: g.options.map((o, idx) => (idx === i ? { ...o, [k]: v } : o)) }));
  const addOpt = () => setEditing((g) => ({ ...g, options: [...g.options, { name: '', price: '' }] }));
  const rmOpt = (i) => setEditing((g) => ({ ...g, options: g.options.filter((_, idx) => idx !== i) }));

  return (
    <div style={{ maxWidth: 820 }}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-800">{t('addOns', 'Add-ons')}</h1>
        <button onClick={() => setEditing(blankGroup())} className="px-4 py-2 rounded-lg font-semibold text-white" style={{ background: GREEN }}>
          + {t('newGroup', 'New group')}
        </button>
      </div>
      <p className="text-sm text-gray-400 mb-5">{t('addOnsHint', 'Create reusable option groups (e.g. Toppings, Size) and attach them to items in the product form.')}</p>

      {isLoading ? (
        <p className="text-gray-400">{t('loading', 'Loading…')}</p>
      ) : groups.length === 0 ? (
        <div className="text-center py-16 text-gray-400">{t('noGroups', 'No add-on groups yet. Create one like "Toppings" or "Size".')}</div>
      ) : (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
          {groups.map((g) => (
            <div key={g._id} className="bg-white border rounded-xl p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="font-bold text-gray-800">{g.name}</span>
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 whitespace-nowrap">
                  {g.multi ? t('pickAny', 'pick any') : t('pickOne', 'pick one')}{g.required ? ' · ' + t('required', 'required') : ''}
                </span>
              </div>
              <div className="mt-2 text-sm text-gray-500 space-y-0.5">
                {(g.options || []).map((o, i) => (
                  <div key={i} className="flex justify-between gap-2">
                    <span className="truncate">{o.name}</span>
                    <span className="whitespace-nowrap">{o.price ? `+$${Number(o.price).toFixed(2)}` : t('free', 'free')}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setEditing({ ...g, options: (g.options || []).map((o) => ({ ...o })) })} className="text-sm font-semibold" style={{ color: GREEN }}>{t('edit', 'Edit')}</button>
                <button onClick={() => { if (window.confirm(t('deleteGroup', 'Delete this group?'))) del.mutate(g._id); }} className="text-sm text-red-500 ml-auto">{t('delete', 'Delete')}</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-md" style={{ maxHeight: '85vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-4">{editing._id ? t('editGroup', 'Edit group') : t('newGroup', 'New group')}</h3>

            <label className="block text-xs font-semibold text-gray-500 mb-1">{t('groupName', 'Group name')}</label>
            <input value={editing.name} onChange={(e) => setField('name', e.target.value)} placeholder="Toppings" className="w-full border rounded-lg px-3 py-2 mb-3" />

            <div className="flex gap-4 mb-4 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.multi} onChange={(e) => setField('multi', e.target.checked)} /> {t('allowMany', 'Allow multiple')}</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.required} onChange={(e) => setField('required', e.target.checked)} /> {t('required', 'Required')}</label>
            </div>

            <label className="block text-xs font-semibold text-gray-500 mb-1">{t('options', 'Options')}</label>
            <div className="space-y-2 mb-3">
              {editing.options.map((o, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input value={o.name} onChange={(e) => setOpt(i, 'name', e.target.value)} placeholder={t('optionName', 'Option name')} className="flex-1 border rounded-lg px-2 py-1.5 text-sm" />
                  <span className="text-gray-400 text-sm">+$</span>
                  <input type="number" step="0.01" value={o.price} onChange={(e) => setOpt(i, 'price', e.target.value)} placeholder="0" className="w-20 border rounded-lg px-2 py-1.5 text-sm" />
                  <button onClick={() => rmOpt(i)} className="text-red-500 px-1">✕</button>
                </div>
              ))}
            </div>
            <button onClick={addOpt} className="text-sm font-semibold mb-4" style={{ color: GREEN }}>+ {t('addOption', 'Add option')}</button>

            <div className="flex gap-2">
              <button onClick={() => setEditing(null)} className="flex-1 py-2.5 rounded-lg border font-semibold">{t('cancel', 'Cancel')}</button>
              <button disabled={!(editing.name || '').trim() || save.isPending} onClick={() => save.mutate(editing)}
                className="flex-1 py-2.5 rounded-lg font-bold text-white disabled:opacity-40" style={{ background: GREEN }}>
                {t('save', 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
